import {EventEmitter, Injectable} from "@angular/core";
import {StripeElements, StripeError, StripeExpressCheckoutElement, StripePaymentElement} from "@stripe/stripe-js";
import {OptionService} from "../option/option.service";
import {FormGroup} from "@angular/forms";

/**
 * Service for using the stripe API and building the payment forms.
 */
@Injectable({
  providedIn: 'root'
})
export class StripeService {
  /**
   * Element for communicationg with the api
   */
  stripe: any;

  /**
   * the payment elements to show the payment inputs.
   */
  elements: StripeElements;

  /**
   * the current payment element.
   */
  paymentElement: StripePaymentElement;

  /**
   * expresscheckout element for google pay and apple pay
   */
  expressPaymentElement: StripeExpressCheckoutElement;

  /**
   * Form to check for validation, before starting express checkout.
   */
  form: FormGroup;

  /**
   * Eventemitter if the stripe form is changed.
   */
  onChangeIsComplete: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Eventhandler when a expressCheckout element is clicked.
   */
  onExpressCheckoutSubmit: EventEmitter<{email: string, firstname: string, lastname: string}> = new EventEmitter<{email: string, firstname: string, lastname: string}>();

  /**
   * Eventemitter if there is an error on the stripe usage.
   */
  onStripeError: EventEmitter<string> = new EventEmitter<string>();

  /**
   * init the stripe service.
   */
  async initStripe() {
    if (!this.stripe) {
      this.loadStripeJS(() => {
        if (window.Stripe) {
          this.stripe = window.Stripe(
            OptionService.getOrganisationDataStatic('stripe_public_key'),
            {stripeAccount: OptionService.getOrganisationDataStatic('stripe_account_id')}
          );
        }
        else {
          console.error('Could not load Stripe, object not available after including the API Script');
        }
      });
    }
  }

  /**
   * Load the stripe javascript into the DOM and call a callback method.
   * @param callback
   */
  loadStripeJS(callback: () => void) {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = callback;

    // Append the script to the document's head
    document.head.appendChild(script);
  }

  /**
   * Init the payment elment for credit cards.
   * @param amount
   * @param currency
   * @param recurring
   * @param id
   */
  initPayment(amount: number, currency: string, recurring: boolean, id: string): void {
    const initResult = this.initPaymentElement(amount, currency, recurring);
    if (initResult === false) {
      return;
    }

    const that = this;

    // Create and mount the Payment Element. hide wallets
    this.paymentElement = this.elements.create('payment', {
      layout: "tabs",
      wallets : {
         applePay: 'never',
         googlePay: 'never'
      }
    });
    // catch change events
    this.paymentElement.on("change", function (event) {
      that.onChangeIsComplete.emit(event.complete)
    });

   // catch errors on stripe loading
    this.paymentElement.on("loaderror", function(event) {
      that.onStripeError.emit('loaderror');
    });

    // mount the element to a given DOM element
    this.paymentElement.mount('#' + id);
  }

  /**
   * Init the expresscheckout elment for credit cards.
   * @param amount
   * @param currency
   * @param id
   * @param wallet Force a wallet taht should be visible
   * @param recurring
   * @param form
   */
  initExpressCheckout(amount: number, currency: string, id: string, wallet: string, recurring: boolean, form: FormGroup): void {
    this.form = form;
    const initResult = this.initPaymentElement(amount, currency, recurring);
    if (initResult === false) {
      return;
    }

    const that = this;

    this.expressPaymentElement = this.elements.create('expressCheckout', {
      buttonType: {
        googlePay: "donate"
      },
      buttonTheme: {
        googlePay: "black"
      },
      buttonHeight: 50,
      paymentMethods: {
         applePay: wallet === 'apple_pay' ? "auto" : "never",
         googlePay: wallet === 'google_pay' ? "auto" : "never",
         link: 'never',
         paypal: 'never',
         amazonPay: 'never'
      },
    });

    // catch errors on stripe loading
    this.expressPaymentElement.on("loaderror", function(event) {
      console.warn('loaderror', JSON.stringify(event))
      that.onStripeError.emit('loaderror');
    });

    // catch errors on the stripe initialization
    this.expressPaymentElement.on("ready", function(event) {
      console.log('ready', JSON.stringify(event))
      // check if the requested payment method is available
      const walletName = wallet.replace(/_([a-z])/g, function(match, letter) {
        return letter.toUpperCase();
      });

      // if event.availablePaymentMethods is undefined, no payment method is available => show error message
      if (!event.availablePaymentMethods) {
        that.onStripeError.emit('paymentnotavailable.' + walletName);
        that.unmountExpressCheckout();
        return;
      }

      if (event.availablePaymentMethods.hasOwnProperty(walletName) === false || event.availablePaymentMethods[walletName] === false) {
        that.onStripeError.emit('paymentnotavailable.' + walletName);
        that.unmountExpressCheckout();
      }
    });

    // handle click event on the express checkout element.
    this.expressPaymentElement.on("click", function(event) {
      if (that.form.valid === true) {
        // ask for email-adress
        event.resolve({
          emailRequired: true,
          shippingAddressRequired: false,
          phoneNumberRequired: false
        });
      }
      else {
        // validate donation form
        that.onExpressCheckoutSubmit.emit({email: null, firstname: null, lastname: null});
      }
    });

    // handle confirmation of express checkout element.
    this.expressPaymentElement.on("confirm", function(event) {
      // extract email and name to donation element.
      const nameParts = event.billingDetails.name ? event.billingDetails.name.split(" ") : [];
      let firstname = null;
      let lastname = null;
      if (nameParts && nameParts.length >= 2) {
        firstname = nameParts[0];
        lastname = nameParts.slice(1).join(" ");
      }

      that.onExpressCheckoutSubmit.emit({email: event.billingDetails.email ?? null, firstname: firstname ?? null, lastname: lastname ?? null});
    });


    // mount the element to a given DOM element
    this.expressPaymentElement.mount('#' + id);
  }

  /**
   * unmount the express checkout element.
   */
  unmountExpressCheckout(): void {
    if (this.expressPaymentElement) {
      this.expressPaymentElement.unmount();
    }
  }

  /**
   * initialize the payment element with the stripe api
   * @param amount
   * @param currency
   * @param recurring
   * @private
   */
  private initPaymentElement(amount: number, currency: string, recurring: boolean): boolean {
    if (!this.stripe) {
      // do noting if stripe is not ready
      return false;
    }

    // Set up Stripe.js and Elements to use in checkout form
    this.elements = this.stripe.elements({
      mode: "payment",
      amount: Math.round(amount * 100),
      paymentMethodConfiguration: OptionService.getOrganisationDataStatic('stripe_payment_method_configuration'),
      currency: currency ? currency.toLowerCase() : "eur",
      fonts: [{
        family: 'Fira Sans',
        src: 'url("/bundles/twinglepublic/fonts/fira-sans-v10-latin-regular.woff")'
      }],
      appearance: {
        theme: 'stripe',
        variables: {
          fontFamily: '"Fira Sans", sans-serif',
          colorPrimary: '#abb0b6',
          colorBackground: '#fff',
          colorText: '#' + OptionService.getOptionStatic('design_label_color'),
          colorDanger: '#E6432A',
          borderRadius: '4px'
        },
        rules: {
          '.Input': {
            borderWidth: '2px',
            borderColor: '#abb0b6',
            color: '#0a0a0a'
          },
          '.Label': {
            fontSize: '11px'
          }
        }
      }
    });

    if (recurring === true) {
      this.elements.update({setupFutureUsage: 'off_session'});
    }

    return true;
  }

  /**
   * Updates the amount in the element.
   * @param newAmount
   */
  updateAmount(newAmount: number): void {
    if (this.elements) {
      this.elements.update({
        amount: Math.round(newAmount * 100)
      });
    }
  }

  /**
   * Updates the recurring option for future usage.
   *
   * @param {boolean} recurring - A flag indicating whether recurring is enabled or not.
   *
   * @return {void}
   */
  updateRecurring(recurring: boolean): void {
    this.elements.update({
      setupFutureUsage: recurring ? 'off_session' : null
    });
  }

  /**
   * Submit the payment to stripe
   */
  submit(): Promise<{error?: StripeError}> {
    return this.elements.submit();
  }

  /**
   * confirm payment on stripe
   * @param clientSecret
   * @param returnUrl
   */
  confirm(clientSecret: string, returnUrl: string) {
    return this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required'
      }
    );
  }
}
